import React, { useState } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import { FiTrash2, FiCopy, FiCheck } from 'react-icons/fi';
import { useAuth } from '../../services/check_login';

const BulkActionsToolbar = ({ selectedCreatives, onActionComplete, allStatuses, statusSystemEnabled }) => {
  const { user: currentUser } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Permissions
  const permissions = currentUser?.role?.permissions || {};
  const canBulkDelete = permissions.canBulkDelete || currentUser?.isAdmin;
  const canBulkEdit = permissions.canBulkEdit || currentUser?.isAdmin;
  const canBulkDuplicate = permissions.canBulkDuplicate || currentUser?.isAdmin;

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const handleBulkDelete = async () => {
    if (selectedCreatives.length === 0) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/bulk-actions/delete', 
        { creativeIds: selectedCreatives },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showNotification(response.data.message, 'success');
      setShowDeleteModal(false);
      onActionComplete();
    } catch (error) {
      console.error('Bulk delete error:', error);
      showNotification(error.response?.data?.error || 'Error deleting creatives', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDuplicate = async () => {
    if (selectedCreatives.length === 0) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/bulk-actions/duplicate', 
        { creativeIds: selectedCreatives },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showNotification(response.data.message, 'success');
      onActionComplete();
    } catch (error) {
      console.error('Bulk duplicate error:', error);
      showNotification(error.response?.data?.error || 'Error duplicating creatives', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (selectedCreatives.length === 0 || !selectedStatus) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/bulk-actions/change-status', 
        { creativeIds: selectedCreatives, statusId: selectedStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showNotification(response.data.message, 'success');
      setShowStatusModal(false);
      setSelectedStatus('');
      onActionComplete();
    } catch (error) {
      console.error('Bulk status change error:', error);
      showNotification(error.response?.data?.error || 'Error changing status', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (selectedCreatives.length === 0) {
    return null;
  }

  return (
    <>
      {/* Notification */}
      {notification.show && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            backgroundColor: notification.type === 'success' ? '#28a745' : '#dc3545',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          {notification.message}
        </div>
      )}

      <div 
        style={{
          position: 'sticky',
          top: '0',
          zIndex: 100,
          backgroundColor: '#ffffff',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}
      >
        <div style={{ fontWeight: '600', color: '#333' }}>
          {selectedCreatives.length} selected
        </div>

        {canBulkDuplicate && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleBulkDuplicate}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FiCopy /> Duplicate
          </Button>
        )}

        {canBulkEdit && statusSystemEnabled && (
          <Button
            variant="info"
            size="sm"
            onClick={() => setShowStatusModal(true)}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FiCheck /> Change Status
          </Button>
        )}

        {canBulkDelete && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FiTrash2 /> Delete
          </Button>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {selectedCreatives.length} creative(s)?</p>
          <p className="text-danger"><strong>This action cannot be undone.</strong></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleBulkDelete} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Change Status Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select new status for {selectedCreatives.length} creative(s):</p>
          <Form.Select 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">-- Select Status --</option>
            {allStatuses.map(status => (
              <option key={status._id} value={status._id}>{status.name}</option>
            ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleStatusChange} 
            disabled={loading || !selectedStatus}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Change Status'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BulkActionsToolbar;
